import { useState, useEffect } from 'react';
import { auth } from '../firebase/firebase';
import {
    signInWithEmailAndPassword,
    getMultiFactorResolver,
    sendPasswordResetEmail,
    TotpMultiFactorGenerator,
} from 'firebase/auth';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ReactComponent as Logo } from '../censuszen-logo.svg';
import '../styles/Auth.css';

const GENERIC_MESSAGE = 'Sign-in failed. Try again.';

const POLICY_MESSAGE =
    'Your password no longer meets the security policy (12 characters with an uppercase letter, ' +
    'a lowercase letter, and a number). Reset it to continue.';

const RESET_SENT_MESSAGE = 'Reset email sent. Follow the link, then sign in with your new password.';

// The resolver offered no TOTP hint. Feeding another factor type a TOTP
// assertion cannot succeed, so the form stays on the password step.
const UNSUPPORTED_FACTOR_MESSAGE =
    'This account uses a second factor this app does not support. Contact your administrator.';

/**
 * Map a Firebase Auth error to a fixed message. The raw error text is never
 * shown to the user. auth/multi-factor-auth-required and
 * auth/password-does-not-meet-requirements are handled by the form itself.
 */
export const messageFor = (err) => {
    switch (err?.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
        case 'auth/invalid-email':
            return 'Email or password is incorrect.';
        case 'auth/too-many-requests':
            return 'Too many attempts. Wait a few minutes and try again.';
        case 'auth/user-disabled':
            return 'This account is disabled. Contact your administrator.';
        case 'auth/network-request-failed':
            return 'Network error. Check your connection and try again.';
        case 'auth/invalid-verification-code':
            return 'That code did not work. Enter the current code from your authenticator app.';
        default:
            return GENERIC_MESSAGE;
    }
};

export const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [pending, setPending] = useState(false);
    // 'password' is the email and password form; 'totp' is the second-factor code form.
    const [step, setStep] = useState('password');
    const [resolver, setResolver] = useState(null);
    const [hint, setHint] = useState(null);
    const [code, setCode] = useState('');
    // Identity Platform forceUpgradeOnSignin: the stored password fails the current policy.
    const [needsReset, setNeedsReset] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const successMessage = location.state?.message;

    // Use useEffect for navigation
    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setNeedsReset(false);
        setResetSent(false);
        setPending(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            navigate('/');
        } catch (err) {
            if (err?.code === 'auth/multi-factor-auth-required') {
                const mfaResolver = getMultiFactorResolver(auth, err);
                const hints = mfaResolver.hints ?? [];
                const totpHint = hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
                if (!totpHint) {
                    setError(UNSUPPORTED_FACTOR_MESSAGE);
                } else {
                    setResolver(mfaResolver);
                    setHint(totpHint);
                    setCode('');
                    setStep('totp');
                }
            } else if (err?.code === 'auth/password-does-not-meet-requirements') {
                setNeedsReset(true);
            } else {
                setError(messageFor(err));
            }
        } finally {
            setPending(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setPending(true);
        try {
            const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
            await resolver.resolveSignIn(assertion);
            navigate('/');
        } catch (err) {
            setError(messageFor(err));
        } finally {
            setPending(false);
        }
    };

    const handleBack = () => {
        setStep('password');
        setResolver(null);
        setHint(null);
        setCode('');
        setError('');
    };

    const handleSendReset = async () => {
        setError('');
        setPending(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setResetSent(true);
        } catch (err) {
            setError(messageFor(err));
        } finally {
            setPending(false);
        }
    };

    const onCodeChange = (e) => {
        setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
    };

    if (step === 'totp') {
        return (
            <div className="auth-page">
                <div className="auth-container">
                    <Link to="/" className="auth-logo-link">
                        <Logo className="auth-logo" aria-label="CensusZen" />
                    </Link>
                    <form onSubmit={handleVerify} className="auth-form">
                        <h2 className="auth-title">Two-factor authentication</h2>
                        <p className="auth-subtitle">Enter the six-digit code from your authenticator app.</p>
                        {error && <div className="auth-error">{error}</div>}
                        <div className="auth-field">
                            <label htmlFor="totp-code">Authenticator code</label>
                            <input
                                id="totp-code"
                                type="text"
                                value={code}
                                onChange={onCodeChange}
                                required
                                minLength={6}
                                maxLength={6}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                autoFocus
                                placeholder="123456"
                            />
                        </div>
                        <button type="submit" className="auth-submit" disabled={pending}>
                            Verify
                        </button>
                        <button
                            type="button"
                            className="auth-secondary"
                            onClick={handleBack}
                            disabled={pending}
                        >
                            Back
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                <Link to="/" className="auth-logo-link">
                    <Logo className="auth-logo" aria-label="CensusZen" />
                </Link>
                <form onSubmit={handleLogin} className="auth-form">
                    <h2 className="auth-title">Welcome back</h2>
                    <p className="auth-subtitle">Sign in to your account</p>
                    {successMessage && <div className="auth-success">{successMessage}</div>}
                    {error && <div className="auth-error">{error}</div>}
                    {needsReset && !resetSent && (
                        <>
                            <div className="auth-error">{POLICY_MESSAGE}</div>
                            <button
                                type="button"
                                className="auth-secondary"
                                onClick={handleSendReset}
                                disabled={pending}
                            >
                                Send password reset email
                            </button>
                        </>
                    )}
                    {resetSent && <div className="auth-success">{RESET_SENT_MESSAGE}</div>}
                    <div className="auth-field">
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="username"
                            placeholder="you@example.com"
                        />
                    </div>
                    <div className="auth-field">
                        <label htmlFor="password">Password</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            placeholder="Enter your password"
                        />
                    </div>
                    <button type="submit" className="auth-submit" disabled={pending}>
                        Sign In
                    </button>
                    <div className="auth-links">
                        <Link to="/forgot-password">Forgot password?</Link>
                        <Link to="/register">Create account</Link>
                    </div>
                </form>
            </div>
        </div>
    );
};
